import React, { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Check, User, X } from "lucide-react";
import { DocumentText, Send2 } from "iconsax-reactjs";
import { toast } from "sonner";
import { OtcRequestItem } from "../../types";
import {
  ProviderItem,
  ProviderSelectionStep,
} from "./ProviderSelectionStep";
// import {
//   AddNewProviderForm,
//   NewProviderData,
// } from "./AddNewProviderForm";
import {
  PaymentInfoData,
  PaymentInformationStep,
} from "./PaymentInformationStep";
import { ReviewTransferDetailsStep } from "./ReviewTransferDetailsStep";
import {
  DomesticRecipient,
  ExternalRecipient,
  settleOtcRequest,
} from "../../services";
import { getApiErrorMessage } from "../../../../shared/lib";

export type WizardStep =
  | "provider"
  | "new_provider"
  | "payment_info"
  | "review";

export interface SendMoneyWizardModalProps {
  open: boolean;
  request: OtcRequestItem;
  onClose: () => void;
  onSubmitSuccess: (data: {
    provider: ProviderItem | null;
    paymentInfo: PaymentInfoData;
  }) => void;
}

export const SendMoneyWizardModal: React.FC<SendMoneyWizardModalProps> = ({
  open,
  request,
  onClose,
  onSubmitSuccess,
}) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>("provider");
  const [selectedProvider, setSelectedProvider] = useState<ProviderItem | null>(
    null
  );
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfoData>({
    note: "",
    invoiceFile: null,
    invoiceFileName: "",
    supportingDocFile: null,
    supportingDocFileName: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetState = () => {
    setCurrentStep("provider");
    setSelectedProvider(null);
    setPaymentInfo({
      note: "",
      invoiceFile: null,
      invoiceFileName: "",
      supportingDocFile: null,
      supportingDocFileName: "",
    });
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleSelectProvider = (prov: ProviderItem) => {
    setSelectedProvider(prov);
  };

  // const handleAddNewProviderConfirm = (data: NewProviderData) => {
  //   const fullName =
  //     data.accountName ||
  //     `${data.firstName} ${data.lastName}`.trim();
  //
  //   const createdProvider: ProviderItem = {
  //     id: `prov-${Date.now()}`,
  //     name: fullName,
  //     accountNumber: data.accountNumber,
  //     companyName: data.bank,
  //     avatarText: fullName.slice(0, 2).toUpperCase(),
  //     bankCode: data.bankCode,
  //     nameEnquiryReference: data.nameEnquiryReference,
  //     relationship: data.relationship,
  //     accountHolderType: data.accountHolderType,
  //     email: data.email,
  //     routingNumber: data.routingNumber,
  //     streetAddress: data.streetAddress,
  //     city: data.city,
  //     state: data.state,
  //     zipCode: data.zipCode,
  //     addressCountry: data.addressCountry,
  //     isDomestic: data.recipientLocation === "Outside the US",
  //   };
  //   setSelectedProvider(createdProvider);
  //   setCurrentStep("payment_info");
  // };

  const handleFinalSubmit = async () => {
    if (!selectedProvider) {
      toast.error("Please select or add a recipient provider before proceeding");
      return;
    }

    const otcIdentifier = request.id || request.transactionId;
    if (!otcIdentifier) {
      toast.error("Invalid OTC Request reference identifier");
      return;
    }

    const isExternalUsd =
      selectedProvider.isDomestic === false ||
      request.currency === "USD" ||
      Boolean(selectedProvider.routingNumber);

    if (isExternalUsd) {
      if (
        !selectedProvider.accountNumber ||
        !selectedProvider.routingNumber ||
        !selectedProvider.name ||
        !selectedProvider.companyName ||
        !selectedProvider.streetAddress ||
        !selectedProvider.city ||
        !selectedProvider.state ||
        !selectedProvider.zipCode
      ) {
        toast.error(
          "Incomplete US recipient details. Account number, routing number, address, and bank details are required."
        );
        return;
      }

      const recipientEmail =
        selectedProvider.email?.trim() || request.customer?.email?.trim();

      if (!recipientEmail) {
        toast.error("Recipient email is required for external USD settlement");
        return;
      }

      setIsSubmitting(true);

      try {
        const externalRecipient: ExternalRecipient = {
          accountNumber: selectedProvider.accountNumber,
          accountName: selectedProvider.name,
          account_owner_name: selectedProvider.name,
          // account_owner_type:
          //   selectedProvider.accountHolderType?.toLowerCase() || "individual",
          // relationship: selectedProvider.relationship?.toLowerCase() || "vendor",
          // email: recipientEmail,
          account_type: "us",
          bank_name: selectedProvider.companyName,
          account: {
            account_number: selectedProvider.accountNumber,
            routing_number: selectedProvider.routingNumber,
            checking_or_savings: "checking",
          },
          address: {
            street_line_1: selectedProvider.streetAddress,
            city: selectedProvider.city,
            state: selectedProvider.state,
            postal_code: selectedProvider.zipCode,
            country: selectedProvider.addressCountry || "US",
          },
          bank_address: {
            street_line_1: selectedProvider.streetAddress,
            city: selectedProvider.city,
            state: selectedProvider.state,
            postal_code: selectedProvider.zipCode,
            country: "US",
          },
        };

        await settleOtcRequest(otcIdentifier, {
          recipient: externalRecipient,
          externalAccountId: selectedProvider.id,
          narration: paymentInfo.note || "OTC USD payout",
        });

        toast.success("OTC settlement payout initiated successfully");
        onSubmitSuccess({
          provider: selectedProvider,
          paymentInfo,
        });
      } catch (error) {
        console.error("OTC settlement error:", error);
        toast.error(
          getApiErrorMessage(
            error,
            "Settlement payout request failed. Please check the recipient bank details and retry."
          )
        );
      } finally {
        setIsSubmitting(false);
      }
    } else {
      if (
        !selectedProvider.accountNumber ||
        !selectedProvider.name ||
        !selectedProvider.bankCode ||
        !selectedProvider.companyName ||
        !selectedProvider.nameEnquiryReference
      ) {
        toast.error(
          "Incomplete domestic recipient details. Account number, verified name, bank code, and name enquiry reference are required."
        );
        return;
      }

      setIsSubmitting(true);

      try {
        const domesticRecipient: DomesticRecipient = {
          accountNumber: selectedProvider.accountNumber,
          accountName: selectedProvider.name,
          bankCode: selectedProvider.bankCode,
          bank_name: selectedProvider.companyName,
          nameEnquiryReference: selectedProvider.nameEnquiryReference,
        };

        await settleOtcRequest(otcIdentifier, {
          recipient: domesticRecipient,
          externalAccountId: selectedProvider.id,
          narration: paymentInfo.note || "OTC payout",
        });

        toast.success("OTC settlement payout initiated successfully");
        onSubmitSuccess({
          provider: selectedProvider,
          paymentInfo,
        });
      } catch (error) {
        console.error("OTC settlement error:", error);
        toast.error(
          getApiErrorMessage(
            error,
            "Settlement payout request failed. Please check the recipient bank details and retry."
          )
        );
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Determine active step index for the 3-step indicator
  const activeStepIdx =
    currentStep === "provider" || currentStep === "new_provider"
      ? 1
      : currentStep === "payment_info"
        ? 2
        : 3;

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs transition-opacity" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[92vh] w-full max-w-[620px] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-6 shadow-xl focus:outline-hidden">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-[#F2F4F7] pb-4">
            <div className="flex items-center gap-3">
              <Dialog.Close asChild>
                <button
                  type="button"
                  onClick={handleClose}
                  className="rounded-lg p-1 text-[#98A2B3] transition-colors hover:bg-[#F2F4F7] hover:text-[#344054]"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </Dialog.Close>
              <Dialog.Title className="text-[16px] font-semibold text-[#101828]">
                Complete OTC transfer
              </Dialog.Title>
            </div>
          </div>

          {/* Stepper (Only visible on the 3 primary wizard steps) */}
          {currentStep !== "new_provider" && (
            <div className="my-6 flex items-center justify-between border-b border-[#F2F4F7] pb-5">
              {/* Step 1 */}
              <div className="flex items-center gap-2">
                <div
                  className={`flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-semibold transition-colors ${
                    activeStepIdx > 1
                      ? "bg-[#6F00FF] text-white"
                      : activeStepIdx === 1
                        ? "bg-[#F9F5FF] text-[#6F00FF] border border-[#6F00FF]"
                        : "bg-[#F2F4F7] text-[#667085]"
                  }`}
                >
                  {activeStepIdx > 1 ? <Check size={14} /> : <User size={13} />}
                </div>
                <span
                  className={`text-[13px] font-medium ${
                    activeStepIdx >= 1 ? "text-[#101828]" : "text-[#667085]"
                  }`}
                >
                  Recipient
                </span>
              </div>

              <div className="h-px flex-1 bg-[#EAECF0] mx-3" />

              {/* Step 2 */}
              <div className="flex items-center gap-2">
                <div
                  className={`flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-semibold transition-colors ${
                    activeStepIdx > 2
                      ? "bg-[#6F00FF] text-white"
                      : activeStepIdx === 2
                        ? "bg-[#F9F5FF] text-[#6F00FF] border border-[#6F00FF]"
                        : "bg-[#F2F4F7] text-[#667085]"
                  }`}
                >
                  {activeStepIdx > 2 ? (
                    <Check size={14} />
                  ) : (
                    <DocumentText size={13} />
                  )}
                </div>
                <span
                  className={`text-[13px] font-medium ${
                    activeStepIdx >= 2 ? "text-[#101828]" : "text-[#667085]"
                  }`}
                >
                  Payment Information
                </span>
              </div>

              <div className="h-px flex-1 bg-[#EAECF0] mx-3" />

              {/* Step 3 */}
              <div className="flex items-center gap-2">
                <div
                  className={`flex h-7 w-7 items-center justify-center rounded-full text-[12px] font-semibold transition-colors ${
                    activeStepIdx === 3
                      ? "bg-[#F9F5FF] text-[#6F00FF] border border-[#6F00FF]"
                      : "bg-[#F2F4F7] text-[#667085]"
                  }`}
                >
                  <Send2 size={13} />
                </div>
                <span
                  className={`text-[13px] font-medium ${
                    activeStepIdx === 3 ? "text-[#101828]" : "text-[#667085]"
                  }`}
                >
                  Review
                </span>
              </div>
            </div>
          )}

          {/* Wizard step views */}
          <div className="mt-2">
            {currentStep === "provider" && (
              <ProviderSelectionStep
                request={request}
                selectedProviderId={selectedProvider?.id || null}
                onSelectProvider={handleSelectProvider}
                onAddNewProviderClick={() => setCurrentStep("new_provider")}
                onBack={handleClose}
                onNext={() => setCurrentStep("payment_info")}
              />
            )}

            {/* Add new provider flow commented out */}
            {/* {currentStep === "new_provider" && (
              <AddNewProviderForm
                request={request}
                onBack={() => setCurrentStep("provider")}
                onConfirm={handleAddNewProviderConfirm}
              />
            )} */}

            {currentStep === "payment_info" && (
              <PaymentInformationStep
                initialData={paymentInfo}
                onBack={() =>
                  setCurrentStep(
                    selectedProvider ? "provider" : "new_provider"
                  )
                }
                onNext={(data) => {
                  setPaymentInfo(data);
                  setCurrentStep("review");
                }}
              />
            )}

            {currentStep === "review" && (
              <ReviewTransferDetailsStep
                request={request}
                provider={selectedProvider}
                paymentInfo={paymentInfo}
                onBack={() => setCurrentStep("payment_info")}
                onSubmit={handleFinalSubmit}
                isSubmitting={isSubmitting}
              />
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
